import { Divider, Stack, Typography } from '@mui/material';
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import { ConditionEditor } from '@/components/conditions/ConditionEditor';
import {
  apps,
  sourceAccount,
  dataClasses,
  environment,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  tags,
  vpc,
  resourceId,
  cloudProvider,
  resourceGroupName,
} from '@/components/queryBuilder/properties';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { type FilterProperty } from '@/types/advanceFilter';

import { type BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { HighFrequencyResourceTypeSelector } from './highFrequencyResourceTypeSelector';
import { HighFrequencySupportedDisclaimer } from './highFrequencySupportedDisclaimer';

import { IncludeExcludeEditor } from '../conditions/includeExcludeEditor';

export const policySupportedProperties: FilterProperty[][] = [
  [resourceType, cloudProvider, dataClasses, environment, apps],
  [sourceAccount, sourceRegion, vpc, subnets, resourceGroupName],
  [resourceName, resourceId, tags],
];

const highFrequencySupportedProperties: FilterProperty[][] = [
  [dataClasses, environment],
  [sourceAccount, sourceRegion],
  [resourceName, resourceId, tags],
];

export const BackupPolicyConditionEditor = (
  props: StepProps<BackupPolicyEditState>
) => {
  const { DynamoDBSupport } = useFeatureFlags();
  const supportedProperties =
    props.wizardState.policyType ===
    BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
      ? highFrequencySupportedProperties
      : policySupportedProperties;

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        stepperLabels={props.stepperLabels}
        stepperIndex={0}
        canGoNext={() =>
          props.wizardState.policyType !==
            BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ||
          !!props.wizardState.resourceTypes?.length
        }
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onChange={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <HighFrequencySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <div className='px-[min(100px,5vw)] pt-[min(60px,3vw)]'>
          {DynamoDBSupport &&
          props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ? (
            <ConditionTitleWithResourceTypeSelector
              resourceTypes={props.wizardState.resourceTypes}
              onChange={(resourceTypes) =>
                props.setWizardState({ ...props.wizardState, resourceTypes })
              }
            />
          ) : (
            <BackupPolicyConditionTitle
              resourceTypes={props.wizardState.resourceTypes}
            />
          )}
          <ConditionEditor
            options={{
              supportGrouping: true,
              supportedProperties,
            }}
            condition={props.wizardState.condition}
            onChange={(condition) =>
              props.setWizardState({ ...props.wizardState, condition })
            }
          />
          <Divider className='my-[40px]' />
          <IncludeExcludeEditor
            wizardState={props.wizardState}
            setWizardState={props.setWizardState}
          />
        </div>
      </StepContainer>
    </>
  );
};

export const BackupPolicyConditionTitle = ({
  resourceTypes,
}: {
  resourceTypes?: ResourceType[];
}) => {
  return (
    <Typography variant='body1' sx={{ mb: '24px' }}>
      Back up{' '}
      {resourceTypes ? (
        <span className='font-semibold'>
          {resourceTypes?.map((x) => ResourceTypes[x].displayName).join(', ')}
          {' resources '}
        </span>
      ) : (
        'resources '
      )}
      based on these conditions:
    </Typography>
  );
};

const ConditionTitleWithResourceTypeSelector = ({
  resourceTypes,
  onChange,
}: {
  resourceTypes?: ResourceType[];
  onChange: (resourceTypes: ResourceType[]) => void;
}) => {
  return (
    <Stack direction='row' alignItems='center' gap='8px' sx={{ mb: '24px' }}>
      <Typography variant='subtitle1'>Back up</Typography>
      {
        <HighFrequencyResourceTypeSelector
          resourceTypes={resourceTypes ?? []}
          onChange={onChange}
        />
      }
      <Typography variant='subtitle1'>
        resources based on these conditions:
      </Typography>
    </Stack>
  );
};
